import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useI18n } from '../i18n';
import { api } from './http';

/**
 * Boxen ein- und ausklappen. Standard: alles offen. Eingeklappte Boxen merkt sich das Tool je Person
 * (PUT /api/me/collapsed), damit der Stand auf allen Geräten gleich ist.
 */
type Ctx = { isOpen: (id: string) => boolean; toggle: (id: string) => void; setOpen: (ids: string[], open: boolean) => void };
const CollapseCtx = createContext<Ctx>({ isOpen: () => true, toggle: () => undefined, setOpen: () => undefined });

export function CollapseProvider({ initial, children }: { initial: string[]; children: ReactNode }) {
  const [closed, setClosed] = useState<Set<string>>(() => new Set(initial));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    // kurz sammeln (z. B. „Alle einklappen“), dann einmal speichern; Fehler sind Nebensache
    timer.current = setTimeout(() => { void api('PUT', '/api/me/collapsed', { ids: [...closed] }).catch(() => undefined); }, 600);
  }, [closed]);
  const value = useMemo<Ctx>(() => ({
    isOpen: (id) => !closed.has(id),
    toggle: (id) => setClosed((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; }),
    setOpen: (ids, open) => setClosed((c) => { const n = new Set(c); for (const id of ids) { if (open) n.delete(id); else n.add(id); } return n; }),
  }), [closed]);
  return <CollapseCtx.Provider value={value}>{children}</CollapseCtx.Provider>;
}

export const useCollapse = () => useContext(CollapseCtx);

/** Kleine Kennzahl in der Kopfzeile einer eingeklappten Box. */
export function Chip({ tone, children }: { tone?: 'pos' | 'neg' | 'crit' | 'info'; children: ReactNode }) {
  return <span className={`cp-chip${tone ? ` ${tone}` : ''}`}>{children}</span>;
}

/**
 * Box mit einklappbarer Kopfzeile. Eingeklappt: nur Titel und `summary` (Chips). `actions` (Schalter, Knöpfe) nur offen.
 * Klick auf die Kopfzeile oder den Pfeil klappt um; Klicks auf Schalter in der Kopfzeile nicht.
 */
export function Panel({ id, title, hint, summary, actions, children, className, headClass }: {
  id: string; title: ReactNode; hint?: ReactNode; summary?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; headClass?: string;
}) {
  const { t } = useI18n();
  const { isOpen, toggle } = useCollapse();
  const open = isOpen(id);
  const bodyId = `cp-${id.replace(/[^a-z0-9]/gi, '-')}`;
  const onHead = useCallback((e: MouseEvent) => {
    if ((e.target as HTMLElement).closest('.cp-actions, a, input, select')) return;
    toggle(id);
  }, [id, toggle]);
  return (
    <div className={`box cp${open ? ' open' : ''}${className ? ` ${className}` : ''}`} data-panel={id}>
      <div className={`panel-h cp-h${headClass ? ` ${headClass}` : ''}`} onClick={onHead}>
        <button type="button" className="cp-chev" aria-expanded={open} aria-controls={bodyId} aria-label={open ? t('cp.collapse') : t('cp.expand')} onClick={(e) => { e.stopPropagation(); toggle(id); }}>
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3.5l4.5 4.5L6 12.5" /></svg>
        </button>
        <h2>{title}</h2>
        {hint && open && <span className="hint">{hint}</span>}
        {!open && summary && <span className="cp-sum">{summary}</span>}
        {open && actions && <><span className="spacer" /><span className="cp-actions">{actions}</span></>}
      </div>
      {open && <div id={bodyId} className="cp-body">{children}</div>}
    </div>
  );
}

/** „Alle einklappen“ / „Alle ausklappen“ für die Boxen einer Seite. */
export function CollapseAll({ ids }: { ids: string[] }) {
  const { t } = useI18n();
  const { isOpen, setOpen } = useCollapse();
  if (!ids.length) return null;
  // alles offen → einklappen; sonst erst einmal alles wieder aufklappen
  const allOpen = ids.every(isOpen);
  return <button type="button" className="btn ghost cp-all" onClick={() => setOpen(ids, !allOpen)}>{allOpen ? t('cp.collapseAll') : t('cp.expandAll')}</button>;
}
